<template>
  <o-dialog
    ref="dialog"
    :title="$t('order.addPasses')"
    :info="$t('components.order.actions.addPasses.info')"
    :options="dialogOptions"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group
          v-slot="{ status }"
          :label="$t('general.customer')"
          :name="$t('general.customer')"
          :info="$t('components.order.actions.addPasses.customerInfo')"
          class="mb-4 block"
          rules="required"
        >
          <customer-select v-model="customer" :status="status" />
        </o-field-group>

        <o-field-group
          v-if="customer"
          v-slot="{ status }"
          :label="$t('general.pass')"
          :name="$t('general.pass')"
          class="block"
          rules="required"
        >
          <div class="block border rounded-lg bg-gray-100 overflow-hidden dark:bg-gray-900">
            <div v-if="!hasPasses && !loadingPasses" class="flex flex-col items-center justify-center mt-6 mb-6">
              <p class="text-sm text-gray-700 dark:text-gray-200">
                {{ $t('components.order.actions.addPasses.noUnpaid', { name: customer?.displayName }) }}
              </p>
            </div>

            <div v-if="loadingPasses" class="flex items-center justify-center gap-2 mt-6 mb-6" role="status">
              <o-loader size="20px" />
              <p class="text-sm text-gray-700 dark:text-gray-200">
                {{ $t('components.order.actions.addPasses.loadingUnpaid', { name: customer?.displayName }) }}
              </p>
            </div>

            <checkbox-group-field
              v-if="hasPasses && !loadingPasses"
              v-model="selected"
              :options="passOptions"
              :status="status"
              search
              class="h-80"
            >
              <template #option="{ option }">
                <div class="w-full ml-3 text-gray-800 pt-1 leading-none dark:text-gray-100">
                  <p class="mb-1">
                    {{ option.value.pass.name }}
                  </p>
                  <p class="text-gray-600 dark:text-gray-300">
                    {{ option.value.infoLabel }}
                  </p>
                </div>
              </template>
            </checkbox-group-field>
          </div>
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { Customer } from '~/_api/models/customer'

import CustomerSelect from '@/components/customer/elements/Select.vue'
import {
  CheckboxGroupField,
} from '@/components/fields'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'update:order', value: any): void
  (e: 'create'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()

const { $api, $notification } = useNuxtApp()
const { t } = useI18n()

const dialog = ref<any>()
const form = ref<any>()

const selected = ref<any[]>([])
const passes = ref<any>(null)
const customer = ref<any>(null)
const loadingPasses = ref(true)
let latestPassesRequest = 0

const dialogOptions = computed(() => {
  if (!props.loading) {
    if (hasPasses.value) {
      return [
        { text: t('general.cancel'), type: 'cancel' },
        { text: t('pass.new'), type: 'confirm', variant: 'white', validate: false, flat: true, outline: true, callback: handleCreateCustomerPass },
        { text: t('components.order.actions.addPasses.addSelectedPass'), type: 'confirm', callback: confirm, disabled: !selected.value.length },
      ]
    } else {
      return [
        { text: t('general.cancel'), type: 'cancel' },
        { text: t('components.order.actions.addPasses.createNewPass'), type: 'confirm', validate: false, callback: handleCreateCustomerPass },
      ]
    }
  }

  return []
})

const passOptions = computed(() => {
  const passesVal = passes.value

  if (!passesVal) { return [] }

  return passesVal
    .map((pass: any) => ({
      value: pass,
      text: pass.pass.name,
      info: pass.infoLabel,
    }))
})

const hasPasses = computed(() => passes.value && !!passOptions.value.length)

watch(customer, () => {
  fetchPasses()
})

function open () {
  customer.value = new Customer({ ...props.order.customer })
  dialog.value.open()
}

async function confirm () {
  emit('update:loading', true)

  try {
    const selectedVal = selected.value
    const response = await $api.order.addCustomerPasses(String(props.order.idOrder), selectedVal.map((p: any) => String(p.idCustomerPass)))
    const order = response.data

    emit('update:order', order)

    $notification.toast({
      title: t('components.order.actions.addPasses.success'),
      text: t('components.order.actions.addPasses.successInfo'),
      variant: 'success',
    })

    emit('update:loading', false)

    return true
  } catch (e) {
    console.log(e)
  }

  emit('update:loading', false)
}

function validate () {
  return form.value?.validate()
}

async function fetchPasses () {
  const requestId = ++latestPassesRequest
  selected.value = []

  const customerVal = customer.value

  if (customerVal) {
    loadingPasses.value = true

    try {
      const response = await $api.customer.passes(customerVal.objectID(), { unpaid: true })

      if (requestId !== latestPassesRequest) return

      passes.value = response.items
    } catch (e) {
      if (requestId !== latestPassesRequest) return

      console.log(e)
      passes.value = []
    } finally {
      if (requestId === latestPassesRequest) {
        loadingPasses.value = false
      }
    }
  } else {
    passes.value = []
    loadingPasses.value = false
  }
}

function handleCreateCustomerPass () {
  emit('create')
  dialog.value.close()
}

defineExpose({ open })
</script>
